import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2, RefreshCw, Activity } from 'lucide-react';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { fmtServerDateTime } from './utils/datetime';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

/**
 * Timeline strip for the SuperAdmin Diagnostics page.
 *
 * Renders one small dot per suite, coloured by the most recent outcome
 * sourced from `db.diagnostic_runs` via `/api/admin/diagnostics/last-runs`.
 * Hovering a dot reveals when it was last run, the trigger (manual /
 * scheduled), and pass/fail counts. Suites that have never been run
 * show a slate dot.
 *
 * The strip is a horizontal row by category — lets the SuperAdmin scan
 * the whole platform health in one glance.
 */
export function LastRunTimeline({ suites, refreshKey }) {
  const [data, setData] = useState({ by_suite: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/diagnostics/last-runs');
      setData(r.data || { by_suite: {} });
    } catch (e) {
      // silent — timeline is auxiliary; missing data should never break the page.
      setData({ by_suite: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  // Group dots by category for visual scanning.
  const grouped = useMemo(() => {
    const order = [
      'Bundles', 'E-Invoicing', 'Sales', 'Purchases', 'Masters',
      'Inventory', 'Reports', 'PDF Engine', 'Licensing & Org', 'Admin Panel', 'Misc',
    ];
    const map = {};
    (suites || []).forEach((s) => {
      const c = s.category || 'Misc';
      (map[c] = map[c] || []).push(s);
    });
    const sorted = order.filter((c) => map[c]).map((c) => [c, map[c]]);
    Object.keys(map).forEach((c) => { if (!order.includes(c)) sorted.push([c, map[c]]); });
    return sorted;
  }, [suites]);

  const stats = useMemo(() => {
    const t = { pass: 0, fail: 0, warn: 0, unrun: 0 };
    (suites || []).forEach((s) => {
      const r = data.by_suite?.[s.key];
      if (!r) t.unrun += 1;
      else if (r.status === 'pass') t.pass += 1;
      else if (r.status === 'fail') t.fail += 1;
      else t.warn += 1;
    });
    return t;
  }, [data, suites]);

  return (
    <section
      className="rounded-xl border border-slate-800 bg-slate-900/50 p-5"
      data-testid="admin-diag-timeline"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-400" /> Last-run timeline
          </h2>
          <p className="text-[11px] text-slate-400 mt-0.5">
            One dot per suite — colour reflects the most recent outcome (any trigger).
            Hover for details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-slate-300 flex items-center gap-2 flex-wrap" data-testid="admin-diag-timeline-stats">
            <Stat colorClass="bg-emerald-500" label="pass" n={stats.pass} />
            <Stat colorClass="bg-rose-500" label="fail" n={stats.fail} />
            <Stat colorClass="bg-amber-500" label="warn" n={stats.warn} />
            <Stat colorClass="bg-slate-600" label="unrun" n={stats.unrun} />
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="text-slate-400 hover:text-slate-100 hover:bg-slate-800"
            data-testid="admin-diag-timeline-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      <TooltipProvider delayDuration={120}>
        <div className="space-y-2">
          {grouped.map(([cat, list]) => (
            <div key={cat} className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 w-28 shrink-0 truncate">
                {cat}
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {list.map((s) => {
                  const r = data.by_suite?.[s.key];
                  return <Dot key={s.key} suite={s} run={r} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </TooltipProvider>
    </section>
  );
}

function Stat({ colorClass, label, n }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-2 h-2 rounded-full ${colorClass}`} />
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-100">{n}</span>
    </span>
  );
}

function Dot({ suite, run }) {
  let bg = 'bg-slate-700 border-slate-600';
  let label = 'Never run';
  if (run) {
    if (run.status === 'pass') { bg = 'bg-emerald-500 border-emerald-300'; label = 'Pass'; }
    else if (run.status === 'fail') { bg = 'bg-rose-500 border-rose-300'; label = 'Fail'; }
    else { bg = 'bg-amber-500 border-amber-300'; label = 'Warn'; }
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`w-3.5 h-3.5 rounded-full border ${bg} transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-amber-400`}
          data-testid={`admin-diag-timeline-dot-${suite.key}`}
          aria-label={`${suite.title} — ${label}`}
        />
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-slate-950 border border-slate-700 text-slate-100 text-xs max-w-xs">
        <div className="space-y-1">
          <p className="font-semibold text-slate-100">{suite.title}</p>
          <p className="text-slate-400">
            <span className={`inline-block w-2 h-2 rounded-full ${bg.split(' ')[0]} mr-1.5`} />
            {label}
          </p>
          {run ? (
            <>
              <p className="text-slate-300 font-mono">
                {run.passed}/{run.total} passed
                {(run.failed + run.errors) > 0 && ` • ${run.failed + run.errors} failed`}
                {' • '}{run.duration_ms}ms
              </p>
              <p className="text-slate-500 text-[11px]">
                {run.trigger === 'scheduled' ? 'Scheduled' : 'Manual'} run · {fmtServerDateTime(run.at)}
              </p>
            </>
          ) : (
            <p className="text-slate-500 text-[11px]">No prior runs recorded.</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export default LastRunTimeline;
